// components/BillingBanner.jsx
// Shows a dismissible top banner for suspended or past_due accounts.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function BillingBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Only relevant for broker owners
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role !== 'owner') return;
    } catch { return; }

    fetch('/api/stripe/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStatus(d); })
      .catch(() => {});
  }, []);

  const openCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const r = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
    } catch { }
    setCheckoutLoading(false);
  };

  if (!status || dismissed) return null;

  const isSuspended = status.status === 'suspended';
  const isPastDue   = status.status === 'past_due';
  const isTrialEndingSoon = status.onTrial && status.trialDaysLeft <= 7 && !status.hasPayment;

  if (!isSuspended && !isPastDue && !isTrialEndingSoon) return null;

  const bg      = isSuspended || isPastDue ? '#dc2626' : '#d97706';
  const message = isSuspended
    ? 'Your free trial has ended and your account needs payment details to stay active.'
    : isPastDue
    ? 'Your last payment failed. Please update your payment details to avoid losing access.'
    : `Your free trial ends in ${status.trialDaysLeft} day${status.trialDaysLeft === 1 ? '' : 's'} — add payment details to continue uninterrupted.`;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: bg, color: '#fff',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
      fontSize: 14, fontWeight: 500,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }}>
      <span>⚠️ {message}</span>
      <button
        onClick={openCheckout}
        disabled={checkoutLoading}
        style={{
          background: '#fff', color: bg, border: 'none',
          padding: '6px 16px', borderRadius: 6,
          fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0,
        }}>
        {checkoutLoading ? 'Opening…' : 'Set up billing →'}
      </button>
      <button
        onClick={() => navigate('/billing')}
        style={{
          background: 'transparent', color: 'rgba(255,255,255,0.8)',
          border: '1px solid rgba(255,255,255,0.4)',
          padding: '6px 14px', borderRadius: 6,
          fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0,
        }}>
        Billing page
      </button>
      <button
        onClick={() => setDismissed(true)}
        style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
          fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
          position: 'absolute', right: 16,
        }}
        title="Dismiss">×</button>
    </div>
  );
}
